import type { PillarPage } from "./content-types";

/**
 * Merchant receipt pages.
 *
 * Purpose: help a human — and an answer engine reading only the text — understand
 * how items actually appear on a given merchant's receipt, and which product
 * ReFound identified for each line.
 *
 * SOURCE OF TRUTH: every code, printed description and identified product name
 * below comes from a ReFound DEMO account and is reproduced verbatim.
 * Rules for editing this file:
 *   - never invent a code, a printed description or a product match;
 *   - never use data from a real user account;
 *   - never state a warranty duration that was not verified;
 *   - always frame observations as "on our demo receipt", never as a universal rule.
 */

const FR = {
  receipts: "/application-recus",
  ocr: "/fonctionnalites/precision-ocr",
  warranties: "/application-garanties",
  returns: "/application-retours-magasin",
  canadianTire: "/marchands/recus-canadian-tire",
  ikea: "/marchands/recus-ikea",
  homeDepot: "/marchands/recus-home-depot",
  rona: "/marchands/recus-rona",
};

const EN = {
  receipts: "/en/receipt-app",
  ocr: "/en/features/ocr-accuracy",
  warranties: "/en/warranty-tracker-app",
  returns: "/en/returns-app",
  canadianTire: "/en/merchants/canadian-tire-receipts",
  ikea: "/en/merchants/ikea-receipts",
  homeDepot: "/en/merchants/home-depot-receipts",
  rona: "/en/merchants/rona-receipts",
};

/** Shared, honest wording about what the demo examples do and do not prove. */
const DISCLAIMER_FR = (merchant: string) => ({
  heading: "À propos de cette page",
  body: [
    `Les exemples présentés ici proviennent d'un compte de démonstration ReFound. Ils montrent ce que l'application a réellement affiché pour ce reçu ${merchant}, et non une règle générale : une même description imprimée peut varier selon le magasin, la période et le produit.`,
    `ReFound est une application indépendante. ReFound n'est ni affiliée, ni approuvée, ni commanditée par ${merchant}. Les noms de marques et de produits sont cités uniquement pour décrire le contenu d'un reçu et appartiennent à leurs propriétaires respectifs.`,
  ],
});

const DISCLAIMER_EN = (merchant: string) => ({
  heading: "About this page",
  body: [
    `The examples on this page come from a ReFound demo account. They show what the app actually displayed for that ${merchant} receipt — they are not a general rule: a printed description can differ from one store, period or product to another.`,
    `ReFound is an independent app. ReFound is not affiliated with, endorsed by or sponsored by ${merchant}. Brand and product names are mentioned only to describe the contents of a receipt and belong to their respective owners.`,
  ],
});

export const merchantPages: PillarPage[] = [
  /* ========================= 1. CANADIAN TIRE ========================= */
  {
    key: "canadian-tire",
    frPath: FR.canadianTire,
    enPath: EN.canadianTire,
    image: "/og-base.png",
    imageAltFr: "Reçu Canadian Tire numérisé dans ReFound",
    imageAltEn: "Canadian Tire receipt digitized in ReFound",
    fr: {
      metaTitle: "Reçus Canadian Tire : comprendre les codes produits | ReFound",
      metaDescription:
        "Sur un reçu Canadian Tire, un article apparaît sous un code (052-2124-0) et une description abrégée (« LAMPE DEL GARAG »). Exemples réels de ce que ReFound identifie.",
      keywords:
        "reçu Canadian Tire, code produit Canadian Tire, comprendre un reçu Canadian Tire, description abrégée reçu, identifier un article sur un reçu, application de reçus",
      breadcrumb: "Reçus Canadian Tire",
      eyebrow: "Marchands",
      h1: "Vos reçus Canadian Tire, plus faciles à comprendre",
      intro: [
        "Sur un reçu Canadian Tire, un article n'apparaît presque jamais sous son nom complet. On y lit un code de produit comme 052-2124-0, suivi d'une description abrégée en majuscules comme « LAMPE DEL GARAG ». Quelques semaines plus tard, ces lignes sont difficiles à relier à ce qu'on a réellement acheté.",
        "Cette page montre, à partir d'un reçu Canadian Tire numérisé dans un compte de démonstration ReFound, comment ces lignes apparaissent à l'impression et quel produit ReFound a identifié pour chacune.",
      ],
      sections: [
        {
          heading: "Comment se présentent les articles sur un reçu Canadian Tire?",
          body: [
            "Sur notre reçu de démonstration Canadian Tire, chaque article occupe une ligne composée de deux éléments : un code de produit en trois groupes de chiffres (par exemple 052-2124-0) et une description abrégée, en majuscules, tronquée à une quinzaine de caractères (par exemple « LAMPE DEL GARAG »).",
            "Cette description est coupée parce que l'espace sur un ruban de caisse est limité. C'est suffisant au moment de payer, mais beaucoup moins utile plus tard, quand il faut retrouver un achat précis, préparer un retour ou valider une garantie.",
          ],
          bullets: [
            "Le code de produit identifie l'article dans le système du marchand",
            "La description imprimée est abrégée, en majuscules et sans accents",
            "Le nom commercial et la marque du produit n'apparaissent pas toujours en clair",
          ],
        },
        {
          heading: "Comment comprendre les codes produits sur un reçu Canadian Tire?",
          body: [
            "Voici cinq articles de notre reçu de démonstration Canadian Tire. Pour chacun, on montre la ligne exacte telle qu'imprimée sur le reçu, puis le produit que ReFound a identifié.",
          ],
          sub: [
            {
              heading: "Que signifie 052-2124-0 sur notre reçu Canadian Tire?",
              body: [
                "Sur notre reçu de démonstration Canadian Tire, le code 052-2124-0 apparaît avec la description abrégée « LAMPE DEL GARAG ». ReFound l'identifie comme une NOMA LED Garage Light, 8000 Lumens.",
              ],
              examples: [
                {
                  code: "052-2124-0",
                  receiptLine: "LAMPE DEL GARAG",
                  productName: "NOMA LED Garage Light, 8000 Lumens",
                  image: {
                    src: "/merchants/ct-052-2124-0.webp",
                    alt: "NOMA LED Garage Light, 8000 Lumens — photo produit associée par ReFound à la ligne « LAMPE DEL GARAG »",
                  },
                  warrantyBadge: "Garantie d'échange 5 ans",
                  warranty:
                    "Dans le compte démo, ReFound affiche pour cet article une garantie d'échange de 5 ans, avec la mention « This product carries a 5 year exchange warranty redeemable at any Canadian Tire store. »",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/noma-led-garage-light-8000-lumens-0522124p.html",
                  merchantUrlLabel: "Voir la fiche sur canadiantire.ca",
                },
              ],
              afterExamples: [
                "« LAMPE DEL GARAG » est la version tronquée de « lampe DEL de garage ». Sans le nom du modèle, impossible de savoir de quelle lampe il s'agit, ni de retrouver sa fiche produit ou ses conditions de garantie. Avec « NOMA LED Garage Light, 8000 Lumens », l'achat reste identifiable des mois plus tard.",
              ],
            },
            {
              heading: "Que signifie 184-2003-4 (« HLT NEO WK 4LB ») sur notre reçu Canadian Tire?",
              body: [
                "Sur notre reçu de démonstration Canadian Tire, le code 184-2003-4 apparaît avec la description abrégée « HLT NEO WK 4LB ». ReFound l'identifie comme un WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights.",
              ],
              examples: [
                {
                  code: "184-2003-4",
                  receiptLine: "HLT NEO WK 4LB",
                  productName:
                    "WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights",
                  image: {
                    src: "/merchants/ct-184-2003-4.webp",
                    alt: "Haltère hexagonal en néoprène WA:KE — photo produit associée par ReFound à la ligne « HLT NEO WK 4LB »",
                  },
                  details: ["Poids indiqué sur la ligne du reçu : 4 lb"],
                  warrantyBadge: "Garantie d'échange 1 an",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/wa-ke-athletic-neoprene-hexagonal-dumbbell-single-assorted-weights-1842000p.html",
                  merchantUrlLabel: "Voir la fiche sur canadiantire.ca",
                },
              ],
              afterExamples: [
                "C'est un bon exemple de description presque impossible à décoder de mémoire. Chaque fragment a pourtant un sens : « HLT » pour haltère, « NEO » pour néoprène, « WK » pour la marque WA:KE et « 4LB » pour le poids. Mis bout à bout, l'ensemble ne ressemble à aucun nom de produit reconnaissable.",
              ],
            },
            {
              heading: "Que signifie 073-5211-4 (« CSQ C CRSTR BE ») sur notre reçu Canadian Tire?",
              body: [
                "Sur notre reçu de démonstration Canadian Tire, le code 073-5211-4 apparaît avec la description abrégée « CSQ C CRSTR BE ». ReFound l'identifie comme un Casque de vélo Supercycle Crosstrails, bébés.",
              ],
              examples: [
                {
                  code: "073-5211-4",
                  receiptLine: "CSQ C CRSTR BE",
                  productName: "Casque de vélo Supercycle Crosstrails, bébés",
                  image: {
                    src: "/merchants/ct-073-5211-4.webp",
                    alt: "Casque de vélo Supercycle Crosstrails pour bébés — photo produit associée par ReFound à la ligne « CSQ C CRSTR BE »",
                  },
                  warrantyBadge: "Garantie d'échange 1 an",
                  merchantUrl:
                    "https://www.canadiantire.ca/fr/pdp/supercycle-infant-crosstrail-bike-helmet-0735210p.html",
                  merchantUrlLabel: "Voir la fiche sur canadiantire.ca",
                },
              ],
            },
            {
              heading: "Que signifie 183-0095-8 (« CASQ+PR-V CCM J ») sur notre reçu Canadian Tire?",
              body: [
                "Sur notre reçu de démonstration Canadian Tire, le code 183-0095-8 apparaît avec la description abrégée « CASQ+PR-V CCM J ». ReFound l'identifie comme un CCM Hockey Helmet & Cage Combo, Youth, Black.",
              ],
              examples: [
                {
                  code: "183-0095-8",
                  receiptLine: "CASQ+PR-V CCM J",
                  productName: "CCM Hockey Helmet & Cage Combo, Youth, Black",
                  image: {
                    src: "/merchants/ct-183-0095-8.webp",
                    alt: "Ensemble casque et protège-visage de hockey CCM pour les jeunes, noir — photo produit associée par ReFound à la ligne « CASQ+PR-V CCM J »",
                  },
                  warrantyBadge: "Garantie : détails en magasin",
                  warranty:
                    "Pour cet article, ReFound affiche « This product carries a special warranty. Please see your local Canadian Tire store for details. » — la durée n'est donc pas indiquée.",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/ccm-hockey-helmet-cage-combo-youth-black-1830095p.html",
                  merchantUrlLabel: "Voir la fiche sur canadiantire.ca",
                },
              ],
              afterExamples: [
                "Ce reçu de démonstration contient deux casques : un casque de vélo pour bébés et un ensemble casque et protège-visage de hockey pour les jeunes. Leurs descriptions imprimées commencent toutes deux par « CAS… », mais il s'agit de deux produits différents, à deux prix différents. C'est exactement le genre de confusion qui complique un retour ou une réclamation quand on ne dispose que du papier.",
              ],
            },
            {
              heading: "Que signifie 153-4141-6 (« PIST ECAL L.SAL ») sur notre reçu Canadian Tire?",
              body: [
                "Sur notre reçu de démonstration Canadian Tire, le code 153-4141-6 apparaît avec la description abrégée « PIST ECAL L.SAL ». ReFound l'identifie comme des Wonderful Pistachios No Shells, Lightly Salted, 170-g.",
              ],
              examples: [
                {
                  code: "153-4141-6",
                  receiptLine: "PIST ECAL L.SAL",
                  productName: "Wonderful Pistachios No Shells, Lightly Salted, 170-g",
                  image: {
                    src: "/merchants/ct-153-4141-6.webp",
                    alt: "Pistaches Wonderful sans écales, légèrement salées, 170 g — photo produit associée par ReFound à la ligne « PIST ECAL L.SAL »",
                  },
                  warrantyBadge: "Garantie d'échange 1 an",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/wonderful-pistachios-no-shells-lightly-salted-170-g-1534141p.html",
                  merchantUrlLabel: "Voir la fiche sur canadiantire.ca",
                },
              ],
              afterExamples: [
                "Un sac de pistaches ne fera généralement pas l'objet d'un retour. L'exemple reste utile : il montre que l'identification ne se limite pas aux gros achats, et qu'un reçu redevient lisible en entier lorsqu'on cherche à vérifier un prix ou à comprendre un montant.",
              ],
            },
          ],
        },
        {
          heading:
            "Pourquoi les descriptions d'un reçu Canadian Tire sont-elles parfois difficiles à reconnaître?",
          body: [
            "Les descriptions imprimées sont conçues pour la caisse, pas pour la mémoire. Sur notre reçu de démonstration, elles tiennent en une quinzaine de caractères, en majuscules, sans accents et fortement abrégées.",
            "Résultat : un même reçu peut contenir deux lignes qui se ressemblent pour deux produits différents, ou une ligne dont presque aucun mot ne correspond au nom commercial du produit. Le code, lui, reste précis — mais il n'a de sens que dans le système du marchand.",
          ],
        },
        {
          heading: "Ce que ReFound ajoute à la simple lecture d'un reçu",
          body: [
            "Un OCR classique lit le texte imprimé et s'arrête là. Il produit « HLT NEO WK 4LB », ce qui est exact, mais reste difficile à comprendre pour la personne qui a fait l'achat.",
            "ReFound part de cette lecture (voir [la précision OCR](/fonctionnalites/precision-ocr)), puis tente d'associer chaque ligne à un produit reconnaissable lorsque l'information est disponible. Sur notre reçu de démonstration Canadian Tire, cinq lignes abrégées deviennent ainsi cinq produits nommés.",
          ],
          bullets: [
            "Le code et la description imprimés sont conservés tels quels",
            "Le nom du produit identifié s'ajoute quand l'information est disponible",
            "Une photo du produit et un lien vers sa fiche chez le marchand s'ajoutent lorsqu'ils existent",
            "L'information de garantie est associée quand elle existe pour ce produit",
            "Le reçu reste utilisable comme preuve d'achat pour un [retour en magasin](/application-retours-magasin)",
          ],
          afterExamples: [
            "L'enrichissement dépend du marchand, du produit et des données disponibles : ReFound n'identifie pas nécessairement tous les articles de tous les reçus.",
          ],
        },
        {
          heading: "Garanties : pourquoi le nom exact du produit change tout",
          body: [
            "Sur notre reçu de démonstration Canadian Tire, les cinq articles ne se ressemblent pas du tout de ce point de vue. Trois d'entre eux portent la même mention d'échange d'un an, et un quatrième (l'ensemble de hockey CCM) renvoie simplement au magasin, sans durée. Le cinquième, l'article 052-2124-0 (« LAMPE DEL GARAG »), porte une garantie d'échange de 5 ans.",
            "Autrement dit, sur ce reçu précis, l'article dont la description imprimée est la plus difficile à décoder est aussi celui dont la garantie est la plus longue — et donc celui qu'il faudra encore pouvoir identifier dans quatre ou cinq ans.",
            "Une réclamation de garantie exige presque toujours deux choses : une preuve d'achat datée et l'identification claire du produit. Une ligne « LAMPE DEL GARAG » ne permet pas d'identifier un modèle; « NOMA LED Garage Light, 8000 Lumens » oui. C'est la différence entre un reçu conservé et un reçu réellement utilisable — voir [le suivi des garanties](/application-garanties).",
          ],
          afterExamples: [
            "Ces durées proviennent de ce que ReFound affichait pour ce reçu de démonstration. Elles ne constituent pas un résumé des conditions de garantie de Canadian Tire : les conditions réelles dépendent du produit et du fabricant, et c'est le marchand qui fait foi.",
          ],
        },
        DISCLAIMER_FR("Canadian Tire"),
      ],
      faqTitle: "Questions fréquentes sur les reçus Canadian Tire",
      faq: [
        {
          q: "Comment savoir à quel produit correspond un code sur mon reçu Canadian Tire?",
          a: "Le code imprimé (par exemple 052-2124-0) identifie l'article dans le système du marchand, mais il n'indique pas le nom du produit. Dans ReFound, le reçu est numérisé, puis la ligne est associée à un produit nommé lorsque l'information est disponible : sur notre reçu de démonstration, 052-2124-0 / « LAMPE DEL GARAG » correspond à la NOMA LED Garage Light, 8000 Lumens.",
        },
        {
          q: "Pourquoi les noms de produits sont-ils abrégés sur un reçu Canadian Tire?",
          a: "L'espace disponible sur un ruban de caisse est limité, alors la description est tronquée à quelques caractères et écrite en majuscules. Sur notre reçu de démonstration, « HLT NEO WK 4LB » correspond ainsi à un WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights.",
        },
        {
          q: "ReFound identifie-t-il tous les articles d'un reçu Canadian Tire?",
          a: "Non. N'importe quel reçu peut être numérisé et conservé, mais l'identification des produits dépend du marchand, du produit et des données disponibles. Sur notre reçu de démonstration Canadian Tire, les cinq articles présentés sur cette page ont été identifiés.",
        },
        {
          q: "ReFound est-il affilié à Canadian Tire?",
          a: "Non. ReFound est une application indépendante, qui n'est ni affiliée, ni approuvée, ni commanditée par Canadian Tire. Cette page décrit seulement la façon dont ReFound traite un reçu de ce marchand.",
        },
      ],
      ctaTitle: "Gardez vos reçus Canadian Tire lisibles",
      ctaBody:
        "Scannez un reçu, gardez-en une copie claire et retrouvez ce que vous avez acheté, même des mois plus tard.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Reçus IKEA", href: FR.ikea, desc: "Trois lignes identiques, trois produits différents" },
        { label: "Reçus RONA", href: FR.rona, desc: "Un thermostat, une garantie et une preuve d'achat" },
        { label: "Reçus Home Depot", href: FR.homeDepot, desc: "Des descriptions difficiles à reconnaître" },
        { label: "Application de reçus", href: FR.receipts, desc: "Ce que fait ReFound, en résumé" },
      ],
    },
    en: {
      metaTitle: "Canadian Tire receipts: what the product codes mean | ReFound",
      metaDescription:
        "On a Canadian Tire receipt, an item shows up as a code (052-2124-0) and a shortened description (\"LAMPE DEL GARAG\"). Real examples of what ReFound identifies.",
      keywords:
        "Canadian Tire receipt, Canadian Tire product code, identify item on receipt, receipt abbreviation meaning, read a Canadian Tire receipt, receipt app",
      breadcrumb: "Canadian Tire receipts",
      eyebrow: "Merchants",
      h1: "Making sense of your Canadian Tire receipts",
      intro: [
        "On a Canadian Tire receipt, items rarely appear under their full name. You get a product code such as 052-2124-0, followed by a shortened, all-caps description such as \"LAMPE DEL GARAG\". A few weeks later, matching those lines to what you actually bought is hard.",
        "This page uses a Canadian Tire receipt scanned into a ReFound demo account to show how each line is printed, and which product ReFound identified for it.",
      ],
      sections: [
        {
          heading: "How do items appear on a Canadian Tire receipt?",
          body: [
            "On our demo Canadian Tire receipt, every item takes one line made of two parts: a product code in three groups of digits (for example 052-2124-0) and a shortened all-caps description, cut to roughly fifteen characters (for example \"LAMPE DEL GARAG\").",
            "The description is truncated because there is very little room on a cash register roll. That works at checkout, but it is far less useful later, when you need to find a specific purchase, prepare a return or make a warranty claim.",
          ],
          bullets: [
            "The product code identifies the item inside the merchant's system",
            "The printed description is abbreviated and written in capitals",
            "The brand and commercial product name are not always spelled out",
          ],
        },
        {
          heading: "How do you read the product codes on a Canadian Tire receipt?",
          body: [
            "Below are five items from our demo Canadian Tire receipt. For each one, you see the line exactly as printed, then the product ReFound identified.",
          ],
          sub: [
            {
              heading: "What does 052-2124-0 mean on our Canadian Tire receipt?",
              body: [
                "On our demo Canadian Tire receipt, the code 052-2124-0 appears with the shortened description \"LAMPE DEL GARAG\". ReFound identifies it as a NOMA LED Garage Light, 8000 Lumens.",
              ],
              examples: [
                {
                  code: "052-2124-0",
                  receiptLine: "LAMPE DEL GARAG",
                  productName: "NOMA LED Garage Light, 8000 Lumens",
                  image: {
                    src: "/merchants/ct-052-2124-0.webp",
                    alt: "NOMA LED Garage Light, 8000 Lumens — product photo ReFound attached to the \"LAMPE DEL GARAG\" line",
                  },
                  warrantyBadge: "5-year exchange warranty",
                  warranty:
                    "In the demo account ReFound shows a 5-year exchange warranty for this item, with the note \"This product carries a 5 year exchange warranty redeemable at any Canadian Tire store.\"",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/noma-led-garage-light-8000-lumens-0522124p.html",
                  merchantUrlLabel: "View on canadiantire.ca",
                },
              ],
              afterExamples: [
                "\"LAMPE DEL GARAG\" is a truncated French description for a LED garage light. Without the model name there is no way to tell which light it was, or to look up its product page or warranty terms. \"NOMA LED Garage Light, 8000 Lumens\" stays identifiable months after the purchase.",
              ],
            },
            {
              heading: "What does 184-2003-4 (\"HLT NEO WK 4LB\") mean on our Canadian Tire receipt?",
              body: [
                "On our demo Canadian Tire receipt, the code 184-2003-4 appears with the shortened description \"HLT NEO WK 4LB\". ReFound identifies it as a WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights.",
              ],
              examples: [
                {
                  code: "184-2003-4",
                  receiptLine: "HLT NEO WK 4LB",
                  productName:
                    "WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights",
                  image: {
                    src: "/merchants/ct-184-2003-4.webp",
                    alt: "WA:KE neoprene hexagonal dumbbell — product photo ReFound attached to the \"HLT NEO WK 4LB\" line",
                  },
                  details: ["Weight shown on the receipt line: 4 lb"],
                  warrantyBadge: "1-year exchange warranty",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/wa-ke-athletic-neoprene-hexagonal-dumbbell-single-assorted-weights-1842000p.html",
                  merchantUrlLabel: "View on canadiantire.ca",
                },
              ],
              afterExamples: [
                "This is a good example of a line almost nobody can decode from memory. Every fragment does mean something — \"HLT\" for haltère (French for dumbbell), \"NEO\" for neoprene, \"WK\" for the WA:KE brand and \"4LB\" for the weight — but strung together they look nothing like a product name.",
              ],
            },
            {
              heading: "What does 073-5211-4 (\"CSQ C CRSTR BE\") mean on our Canadian Tire receipt?",
              body: [
                "On our demo Canadian Tire receipt, the code 073-5211-4 appears with the shortened description \"CSQ C CRSTR BE\". ReFound identifies it as a Supercycle Infant Crosstrail Bike Helmet.",
              ],
              examples: [
                {
                  code: "073-5211-4",
                  receiptLine: "CSQ C CRSTR BE",
                  productName: "Supercycle Infant Crosstrail Bike Helmet",
                  image: {
                    src: "/merchants/ct-073-5211-4.webp",
                    alt: "Supercycle Crosstrails infant bike helmet — product photo ReFound attached to the \"CSQ C CRSTR BE\" line",
                  },
                  warrantyBadge: "1-year exchange warranty",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/supercycle-infant-crosstrail-bike-helmet-0735210p.html",
                  merchantUrlLabel: "View on canadiantire.ca",
                },
              ],
            },
            {
              heading: "What does 183-0095-8 (\"CASQ+PR-V CCM J\") mean on our Canadian Tire receipt?",
              body: [
                "On our demo Canadian Tire receipt, the code 183-0095-8 appears with the shortened description \"CASQ+PR-V CCM J\". ReFound identifies it as a CCM Hockey Helmet & Cage Combo, Youth, Black.",
              ],
              examples: [
                {
                  code: "183-0095-8",
                  receiptLine: "CASQ+PR-V CCM J",
                  productName: "CCM Hockey Helmet & Cage Combo, Youth, Black",
                  image: {
                    src: "/merchants/ct-183-0095-8.webp",
                    alt: "CCM youth hockey helmet and cage combo in black — product photo ReFound attached to the \"CASQ+PR-V CCM J\" line",
                  },
                  warrantyBadge: "Warranty: details in store",
                  warranty:
                    "For this item ReFound shows \"This product carries a special warranty. Please see your local Canadian Tire store for details.\" — so no duration is stated.",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/ccm-hockey-helmet-cage-combo-youth-black-1830095p.html",
                  merchantUrlLabel: "View on canadiantire.ca",
                },
              ],
              afterExamples: [
                "This demo receipt contains two helmets: a baby bike helmet and a youth hockey helmet-and-cage combo. Both printed descriptions start with \"CAS…\", yet they are two different products at two different prices. That is exactly the kind of confusion that makes a return or a warranty claim harder when all you have is the paper slip.",
              ],
            },
            {
              heading: "What does 153-4141-6 (\"PIST ECAL L.SAL\") mean on our Canadian Tire receipt?",
              body: [
                "On our demo Canadian Tire receipt, the code 153-4141-6 appears with the shortened description \"PIST ECAL L.SAL\". ReFound identifies it as Wonderful Pistachios No Shells, Lightly Salted, 170-g.",
              ],
              examples: [
                {
                  code: "153-4141-6",
                  receiptLine: "PIST ECAL L.SAL",
                  productName: "Wonderful Pistachios No Shells, Lightly Salted, 170-g",
                  image: {
                    src: "/merchants/ct-153-4141-6.webp",
                    alt: "Wonderful Pistachios No Shells, Lightly Salted, 170 g — product photo ReFound attached to the \"PIST ECAL L.SAL\" line",
                  },
                  warrantyBadge: "1-year exchange warranty",
                  merchantUrl:
                    "https://www.canadiantire.ca/en/pdp/wonderful-pistachios-no-shells-lightly-salted-170-g-1534141p.html",
                  merchantUrlLabel: "View on canadiantire.ca",
                },
              ],
              afterExamples: [
                "Nobody returns a bag of pistachios. The example still matters: it shows that identification is not limited to big-ticket items, and that a whole receipt becomes readable again when you are checking a price or trying to explain a total.",
              ],
            },
          ],
        },
        {
          heading: "Why are Canadian Tire receipt descriptions hard to recognize?",
          body: [
            "Printed descriptions are designed for the checkout, not for your memory. On our demo receipt they fit in about fifteen characters, in capitals, heavily abbreviated — and several are abbreviated French.",
            "The result: one receipt can hold two near-identical lines for two different products, or a line where almost no word matches the product's commercial name. The code stays precise, but it only means something inside the merchant's own system.",
          ],
        },
        {
          heading: "What ReFound adds on top of plain receipt reading",
          body: [
            "Standard OCR reads the printed text and stops there. It returns \"HLT NEO WK 4LB\" — accurate, and still unhelpful to the person who made the purchase.",
            "ReFound starts from that reading (see [OCR accuracy](/en/features/ocr-accuracy)), then tries to match each line to a recognizable product when the information is available. On our demo Canadian Tire receipt, five abbreviated lines become five named products.",
          ],
          bullets: [
            "The printed code and description are kept exactly as they are",
            "The identified product name is added when the information is available",
            "A product photo and a link to the merchant's product page are added when they exist",
            "Warranty information is attached when it exists for that product",
            "The receipt stays usable as proof of purchase for an [in-store return](/en/returns-app)",
          ],
          afterExamples: [
            "Enrichment depends on the merchant, the product and the data available: ReFound does not necessarily identify every item on every receipt.",
          ],
        },
        {
          heading: "Warranties: why the exact product name matters",
          body: [
            "On our demo Canadian Tire receipt the five items are far from equal on this point. Three of them carry the same one-year exchange note, and a fourth (the CCM hockey combo) simply points back to the store, with no duration. The fifth, item 052-2124-0 (\"LAMPE DEL GARAG\"), carries a 5-year exchange warranty.",
            "In other words, on this particular receipt, the item with the hardest printed description to decode is also the one with the longest warranty — the one you will still need to identify four or five years from now.",
            "A warranty claim almost always needs two things: dated proof of purchase and a clear identification of the product. \"LAMPE DEL GARAG\" does not identify a model; \"NOMA LED Garage Light, 8000 Lumens\" does. That is the difference between a receipt you kept and a receipt you can actually use — see [warranty tracking](/en/warranty-tracker-app).",
          ],
          afterExamples: [
            "These durations are what ReFound displayed for this demo receipt. They are not a summary of Canadian Tire's warranty terms: actual coverage depends on the product and the manufacturer, and the merchant is the authority on it.",
          ],
        },
        DISCLAIMER_EN("Canadian Tire"),
      ],
      faqTitle: "Canadian Tire receipt questions",
      faq: [
        {
          q: "How do I find out which product a code on my Canadian Tire receipt refers to?",
          a: "The printed code (for example 052-2124-0) identifies the item inside the merchant's system, but it does not tell you the product name. In ReFound the receipt is digitized, then the line is matched to a named product when the information is available: on our demo receipt, 052-2124-0 / \"LAMPE DEL GARAG\" is a NOMA LED Garage Light, 8000 Lumens.",
        },
        {
          q: "Why are product names abbreviated on a Canadian Tire receipt?",
          a: "There is very little room on a cash register roll, so descriptions are cut to a few characters and printed in capitals. On our demo receipt, \"HLT NEO WK 4LB\" is a WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights.",
        },
        {
          q: "Does ReFound identify every item on a Canadian Tire receipt?",
          a: "No. Any receipt can be scanned and stored, but product identification depends on the merchant, the product and the data available. On our demo Canadian Tire receipt, the five items shown on this page were identified.",
        },
        {
          q: "Is ReFound affiliated with Canadian Tire?",
          a: "No. ReFound is an independent app, not affiliated with, endorsed by or sponsored by Canadian Tire. This page only describes how ReFound handles a receipt from that merchant.",
        },
      ],
      ctaTitle: "Keep your Canadian Tire receipts readable",
      ctaBody:
        "Scan a receipt, keep a clear copy, and still know what you bought months later.",
      relatedTitle: "Read next",
      related: [
        { label: "IKEA receipts", href: EN.ikea, desc: "Three identical lines, three different products" },
        { label: "RONA receipts", href: EN.rona, desc: "A thermostat, a warranty and proof of purchase" },
        { label: "Home Depot receipts", href: EN.homeDepot, desc: "Descriptions that are hard to recognize" },
        { label: "Receipt app", href: EN.receipts, desc: "What ReFound does, in short" },
      ],
    },
  },

  /* ============================== 2. IKEA ============================== */
  {
    key: "ikea",
    frPath: FR.ikea,
    enPath: EN.ikea,
    image: "/og-base.png",
    imageAltFr: "Reçu IKEA numérisé dans ReFound",
    imageAltEn: "IKEA receipt digitized in ReFound",
    fr: {
      metaTitle: "Reçus IKEA : trois lignes identiques, trois produits | ReFound",
      metaDescription:
        "Sur notre reçu IKEA de démonstration, trois lignes « VÅGSJÖN washclth 30x » identiques correspondent à trois débarbouillettes de couleurs différentes identifiées par ReFound.",
      keywords:
        "reçu IKEA, comprendre un reçu IKEA, noms abrégés reçu IKEA, VÅGSJÖN, identifier un article sur un reçu, application de reçus",
      breadcrumb: "Reçus IKEA",
      eyebrow: "Marchands",
      h1: "Vos reçus IKEA, plus faciles à comprendre",
      intro: [
        "Les produits IKEA portent un nom de série — VÅGSJÖN, AFTONSPARV, SMÅÄTA — qui ne dit rien de la couleur ni du format. Sur le reçu, ce nom est suivi d'une description abrégée et tronquée.",
        "Sur notre reçu IKEA de démonstration, cela produit un cas très clair : trois lignes visuellement identiques correspondent à trois produits différents. Voici comment elles sont imprimées et ce que ReFound a identifié.",
      ],
      sections: [
        {
          heading: "Pourquoi trois lignes identiques d'un reçu IKEA correspondent à trois produits différents",
          body: [
            "Sur notre reçu IKEA de démonstration, la ligne « VÅGSJÖN washclth 30x » apparaît trois fois, à l'identique. À la lecture du papier, rien ne distingue les trois articles.",
            "Dans ReFound, ces trois lignes correspondent à trois débarbouillettes VÅGSJÖN de couleurs différentes :",
          ],
          examples: [
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, blanc, 12x12 \"",
              image: {
                src: "/merchants/ikea-10350998.webp",
                alt: "Débarbouillette VÅGSJÖN blanche — photo produit associée par ReFound à la première ligne « VÅGSJÖN washclth 30x »",
              },
              merchantUrl: "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-blanc-10350998/",
              merchantUrlLabel: "Voir la fiche sur ikea.com",
            },
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, beige clair, 12x12 \"",
              image: {
                src: "/merchants/ikea-80494620.webp",
                alt: "Débarbouillette VÅGSJÖN beige clair — photo produit associée par ReFound à la deuxième ligne « VÅGSJÖN washclth 30x »",
              },
              merchantUrl:
                "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-beige-clair-80494620/",
              merchantUrlLabel: "Voir la fiche sur ikea.com",
            },
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, gris foncé, 12x12 \"",
              image: {
                src: "/merchants/ikea-40353622.webp",
                alt: "Débarbouillette VÅGSJÖN gris foncé — photo produit associée par ReFound à la troisième ligne « VÅGSJÖN washclth 30x »",
              },
              merchantUrl:
                "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-gris-fonce-40353622/",
              merchantUrlLabel: "Voir la fiche sur ikea.com",
            },
          ],
          afterExamples: [
            "Autrement dit : sur notre reçu IKEA de démonstration, les trois lignes « VÅGSJÖN washclth 30x » correspondent respectivement à une VÅGSJÖN Débarbouillette blanc, une beige clair et une gris foncé, toutes en 12x12 pouces.",
            "La couleur ne figure nulle part dans la description imprimée. C'est la démonstration la plus nette de la différence entre lire le texte d'un reçu et rendre un reçu réellement compréhensible : un OCR parfait produirait ici trois fois exactement la même chaîne de caractères.",
          ],
        },
        {
          heading: "Comment se présentent les articles sur un reçu IKEA?",
          body: [
            "Sur notre reçu IKEA de démonstration, chaque ligne combine le nom de série du produit, écrit en majuscules avec ses caractères suédois, et une description fonctionnelle abrégée en anglais, coupée en fin de ligne — d'où des fins de mots tronquées comme « washclth 30x » ou « dvt cvr+p ».",
            "Le nom de série identifie une gamme, pas un article précis. Une même gamme peut exister en plusieurs couleurs, formats et finis, sans que le reçu les distingue.",
          ],
          bullets: [
            "Le nom de série (VÅGSJÖN, AFTONSPARV…) désigne une gamme, pas une variante",
            "La description imprimée est abrégée et tronquée en fin de ligne",
            "La couleur et le fini n'apparaissent pas dans la description imprimée",
          ],
        },
        {
          heading: "Autres exemples de notre reçu IKEA de démonstration",
          body: [
            "Les trois articles suivants proviennent du même reçu de démonstration. Ils montrent des descriptions dont il ne reste, à l'impression, que des fragments de mots.",
          ],
          sub: [
            {
              heading: "Que signifie « AFTONSPARV dvt cvr+p » sur notre reçu IKEA?",
              body: [
                "Sur notre reçu IKEA de démonstration, la ligne « AFTONSPARV dvt cvr+p » est identifiée par ReFound comme une AFTONSPARV Housse de couette et 1 taie, espace/multicolore, 1 place.",
              ],
              examples: [
                {
                  receiptLine: "AFTONSPARV dvt cvr+p",
                  productName: "AFTONSPARV Housse de couette et 1 taie, espace/multicolore, 1 place",
                  image: {
                    src: "/merchants/ikea-90554053.webp",
                    alt: "Housse de couette AFTONSPARV, motif espace multicolore — photo produit associée par ReFound à la ligne « AFTONSPARV dvt cvr+p »",
                  },
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/aftonsparv-housse-de-couette-et-1-taie-espace-multicolore-90554053/",
                  merchantUrlLabel: "Voir la fiche sur ikea.com",
                },
              ],
              afterExamples: [
                "« dvt cvr+p » est la contraction de « duvet cover + pillowcase ». Le motif (espace), les couleurs et le format (1 place) n'apparaissent que dans le produit identifié.",
              ],
            },
            {
              heading: "Que signifie « SMÅÄTA chop brd 28x2 » sur notre reçu IKEA?",
              body: [
                "Sur notre reçu IKEA de démonstration, la ligne « SMÅÄTA chop brd 28x2 » est identifiée par ReFound comme une SMÅÄTA Planche à découper, acacia, 11x8 ¾ pouces.",
              ],
              examples: [
                {
                  receiptLine: "SMÅÄTA chop brd 28x2",
                  productName: "SMÅÄTA Planche à découper, acacia, 11x8 ¾ \"",
                  image: {
                    src: "/merchants/ikea-20557168.webp",
                    alt: "Planche à découper SMÅÄTA en acacia — photo produit associée par ReFound à la ligne « SMÅÄTA chop brd 28x2 »",
                  },
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/smaaeta-planche-a-decouper-acacia-20557168/",
                  merchantUrlLabel: "Voir la fiche sur ikea.com",
                },
              ],
            },
            {
              heading: "Que signifie « EKOLN soap dispnsr b » sur notre reçu IKEA?",
              body: [
                "Sur notre reçu IKEA de démonstration, la ligne « EKOLN soap dispnsr b » est identifiée par ReFound comme un EKOLN Distributeur de savon, beige.",
              ],
              examples: [
                {
                  receiptLine: "EKOLN soap dispnsr b",
                  productName: "EKOLN Distributeur de savon, beige",
                  image: {
                    src: "/merchants/ikea-60493004.webp",
                    alt: "Distributeur de savon EKOLN beige — photo produit associée par ReFound à la ligne « EKOLN soap dispnsr b »",
                  },
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/ekoln-distributeur-de-savon-beige-60493004/",
                  merchantUrlLabel: "Voir la fiche sur ikea.com",
                },
              ],
              afterExamples: [
                "Ici, le « b » final de la description imprimée est tout ce qui reste de la couleur. Le produit identifié la rend explicite.",
              ],
            },
          ],
        },
        {
          heading: "Ce que ReFound ajoute à la simple lecture d'un reçu",
          body: [
            "Lire correctement « VÅGSJÖN washclth 30x » est un travail d'OCR (voir [la précision OCR](/fonctionnalites/precision-ocr)). Savoir laquelle des trois débarbouillettes était la grise est un autre travail.",
            "ReFound conserve la ligne imprimée telle quelle, puis y associe un produit reconnaissable lorsque l'information est disponible — avec, dans ce cas-ci, une photo du produit et un lien vers sa fiche chez le marchand. Le reçu reste une [preuve d'achat](/application-recus) valable, mais devient aussi une liste d'achats lisible.",
          ],
          afterExamples: [
            "L'identification dépend du marchand, du produit et des données disponibles : ReFound n'identifie pas nécessairement tous les articles de tous les reçus.",
          ],
        },
        DISCLAIMER_FR("IKEA"),
      ],
      faqTitle: "Questions fréquentes sur les reçus IKEA",
      faq: [
        {
          q: "Pourquoi les noms des produits sont-ils abrégés sur mon reçu IKEA?",
          a: "L'espace d'impression est limité, alors la description est tronquée en fin de ligne. Sur notre reçu de démonstration, cela donne des fragments comme « washclth 30x » ou « dvt cvr+p », qui correspondent respectivement à une débarbouillette et à une housse de couette avec taie.",
        },
        {
          q: "Comment savoir de quelle couleur était un article sur un reçu IKEA?",
          a: "La couleur n'apparaît pas dans la description imprimée de notre reçu de démonstration. C'est pourquoi trois lignes « VÅGSJÖN washclth 30x » identiques correspondent en réalité à une débarbouillette blanche, une beige clair et une gris foncé — une distinction que ReFound rend visible lorsque l'information est disponible.",
        },
        {
          q: "ReFound fonctionne-t-il avec les reçus IKEA?",
          a: "Oui. Un reçu IKEA peut être numérisé et conservé comme n'importe quel autre reçu, et les articles sont associés à un produit nommé lorsque l'information est disponible, comme dans les exemples de cette page.",
        },
        {
          q: "ReFound est-il affilié à IKEA?",
          a: "Non. ReFound est une application indépendante, qui n'est ni affiliée, ni approuvée, ni commanditée par IKEA. Cette page décrit seulement la façon dont ReFound traite un reçu de ce marchand.",
        },
      ],
      ctaTitle: "Sachez ce que contient vraiment votre reçu IKEA",
      ctaBody:
        "Numérisez le reçu, gardez-en une copie claire et retrouvez chaque article, couleur comprise, quand l'information est disponible.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Reçus Canadian Tire", href: FR.canadianTire, desc: "Décoder les codes produits" },
        { label: "Reçus RONA", href: FR.rona, desc: "Un thermostat, une garantie et une preuve d'achat" },
        { label: "Reçus Home Depot", href: FR.homeDepot, desc: "Des descriptions difficiles à reconnaître" },
        { label: "Application de reçus", href: FR.receipts, desc: "Ce que fait ReFound, en résumé" },
      ],
    },
    en: {
      metaTitle: "IKEA receipts: three identical lines, three products | ReFound",
      metaDescription:
        "On our demo IKEA receipt, three identical \"VÅGSJÖN washclth 30x\" lines turn out to be three washcloths in different colours, each identified by ReFound.",
      keywords:
        "IKEA receipt, read an IKEA receipt, IKEA receipt abbreviations, VÅGSJÖN, identify item on receipt, receipt app",
      breadcrumb: "IKEA receipts",
      eyebrow: "Merchants",
      h1: "Making sense of your IKEA receipts",
      intro: [
        "IKEA products carry a series name — VÅGSJÖN, AFTONSPARV, SMÅÄTA — that says nothing about colour or size. On the receipt, that name is followed by a shortened, truncated description.",
        "On our demo IKEA receipt this produces a very clear case: three visually identical lines that are actually three different products. Here is how they were printed, and what ReFound identified.",
      ],
      sections: [
        {
          heading: "Why three identical IKEA receipt lines are three different products",
          body: [
            "On our demo IKEA receipt, the line \"VÅGSJÖN washclth 30x\" appears three times, character for character. On paper, nothing tells the three items apart.",
            "In ReFound, those three lines are three VÅGSJÖN washcloths in different colours:",
          ],
          examples: [
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, blanc, 12x12 \"",
              image: {
                src: "/merchants/ikea-10350998.webp",
                alt: "White VÅGSJÖN washcloth — product photo ReFound attached to the first \"VÅGSJÖN washclth 30x\" line",
              },
              details: ["VÅGSJÖN washcloth, white, 12x12\""],
              merchantUrl: "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-blanc-10350998/",
              merchantUrlLabel: "View on ikea.com",
            },
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, beige clair, 12x12 \"",
              image: {
                src: "/merchants/ikea-80494620.webp",
                alt: "Light beige VÅGSJÖN washcloth — product photo ReFound attached to the second \"VÅGSJÖN washclth 30x\" line",
              },
              details: ["VÅGSJÖN washcloth, light beige, 12x12\""],
              merchantUrl:
                "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-beige-clair-80494620/",
              merchantUrlLabel: "View on ikea.com",
            },
            {
              receiptLine: "VÅGSJÖN washclth 30x",
              productName: "VÅGSJÖN Débarbouillette, gris foncé, 12x12 \"",
              image: {
                src: "/merchants/ikea-40353622.webp",
                alt: "Dark grey VÅGSJÖN washcloth — product photo ReFound attached to the third \"VÅGSJÖN washclth 30x\" line",
              },
              details: ["VÅGSJÖN washcloth, dark grey, 12x12\""],
              merchantUrl:
                "https://www.ikea.com/ca/fr/p/vagsjoen-debarbouillette-gris-fonce-40353622/",
              merchantUrlLabel: "View on ikea.com",
            },
          ],
          afterExamples: [
            "In other words: on our demo IKEA receipt, the three \"VÅGSJÖN washclth 30x\" lines are a white VÅGSJÖN washcloth, a light beige one and a dark grey one, all 12x12 inches. (The product names were displayed in French in this demo account, which is how they are reproduced above.)",
            "The colour appears nowhere in the printed description. This is the clearest illustration of the gap between reading a receipt's text and making a receipt understandable: perfect OCR would return the exact same string three times.",
          ],
        },
        {
          heading: "How do items appear on an IKEA receipt?",
          body: [
            "On our demo IKEA receipt, each line combines the product's series name — in capitals, with its Swedish characters — and a shortened functional description in English, cut off at the end of the line. That is where fragments like \"washclth 30x\" or \"dvt cvr+p\" come from.",
            "The series name identifies a range, not a specific article. One range can exist in several colours, sizes and finishes, none of which the receipt distinguishes.",
          ],
          bullets: [
            "The series name (VÅGSJÖN, AFTONSPARV…) is a range, not a variant",
            "The printed description is abbreviated and truncated at the end of the line",
            "Colour and finish do not appear in the printed description",
          ],
        },
        {
          heading: "More examples from our demo IKEA receipt",
          body: [
            "The three items below come from the same demo receipt. In each case, only fragments of words survive the printing.",
          ],
          sub: [
            {
              heading: "What does \"AFTONSPARV dvt cvr+p\" mean on our IKEA receipt?",
              body: [
                "On our demo IKEA receipt, the line \"AFTONSPARV dvt cvr+p\" is identified by ReFound as an AFTONSPARV Housse de couette et 1 taie, espace/multicolore, 1 place — an AFTONSPARV duvet cover and one pillowcase, space/multicolour, twin size.",
              ],
              examples: [
                {
                  receiptLine: "AFTONSPARV dvt cvr+p",
                  productName: "AFTONSPARV Housse de couette et 1 taie, espace/multicolore, 1 place",
                  image: {
                    src: "/merchants/ikea-90554053.webp",
                    alt: "AFTONSPARV duvet cover with a multicoloured space pattern — product photo ReFound attached to the \"AFTONSPARV dvt cvr+p\" line",
                  },
                  details: ["Duvet cover and 1 pillowcase, space/multicolour, twin"],
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/aftonsparv-housse-de-couette-et-1-taie-espace-multicolore-90554053/",
                  merchantUrlLabel: "View on ikea.com",
                },
              ],
              afterExamples: [
                "\"dvt cvr+p\" is a contraction of \"duvet cover + pillowcase\". The pattern, the colours and the bed size only show up in the identified product.",
              ],
            },
            {
              heading: "What does \"SMÅÄTA chop brd 28x2\" mean on our IKEA receipt?",
              body: [
                "On our demo IKEA receipt, the line \"SMÅÄTA chop brd 28x2\" is identified by ReFound as a SMÅÄTA Planche à découper, acacia, 11x8 ¾ \" — a SMÅÄTA acacia chopping board measuring 11 by 8¾ inches.",
              ],
              examples: [
                {
                  receiptLine: "SMÅÄTA chop brd 28x2",
                  productName: "SMÅÄTA Planche à découper, acacia, 11x8 ¾ \"",
                  image: {
                    src: "/merchants/ikea-20557168.webp",
                    alt: "SMÅÄTA acacia chopping board — product photo ReFound attached to the \"SMÅÄTA chop brd 28x2\" line",
                  },
                  details: ["Chopping board, acacia, 11x8 ¾ in"],
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/smaaeta-planche-a-decouper-acacia-20557168/",
                  merchantUrlLabel: "View on ikea.com",
                },
              ],
            },
            {
              heading: "What does \"EKOLN soap dispnsr b\" mean on our IKEA receipt?",
              body: [
                "On our demo IKEA receipt, the line \"EKOLN soap dispnsr b\" is identified by ReFound as an EKOLN Distributeur de savon, beige — an EKOLN soap dispenser in beige.",
              ],
              examples: [
                {
                  receiptLine: "EKOLN soap dispnsr b",
                  productName: "EKOLN Distributeur de savon, beige",
                  image: {
                    src: "/merchants/ikea-60493004.webp",
                    alt: "Beige EKOLN soap dispenser — product photo ReFound attached to the \"EKOLN soap dispnsr b\" line",
                  },
                  details: ["Soap dispenser, beige"],
                  merchantUrl:
                    "https://www.ikea.com/ca/fr/p/ekoln-distributeur-de-savon-beige-60493004/",
                  merchantUrlLabel: "View on ikea.com",
                },
              ],
              afterExamples: [
                "Here the trailing \"b\" is all that is left of the colour. The identified product spells it out.",
              ],
            },
          ],
        },
        {
          heading: "What ReFound adds on top of plain receipt reading",
          body: [
            "Reading \"VÅGSJÖN washclth 30x\" correctly is an OCR job (see [OCR accuracy](/en/features/ocr-accuracy)). Knowing which of the three washcloths was the grey one is a different job.",
            "ReFound keeps the printed line exactly as it is, then attaches a recognizable product when the information is available — here with a product photo and a link to the merchant's product page. The receipt stays valid [proof of purchase](/en/receipt-app), and also becomes a readable shopping list.",
          ],
          afterExamples: [
            "Identification depends on the merchant, the product and the data available: ReFound does not necessarily identify every item on every receipt.",
          ],
        },
        DISCLAIMER_EN("IKEA"),
      ],
      faqTitle: "IKEA receipt questions",
      faq: [
        {
          q: "Why are product names abbreviated on an IKEA receipt?",
          a: "Printing space is limited, so the description is cut off at the end of the line. On our demo receipt that leaves fragments such as \"washclth 30x\" or \"dvt cvr+p\", which are a washcloth and a duvet cover with pillowcase respectively.",
        },
        {
          q: "How can I tell what colour an item on an IKEA receipt was?",
          a: "The colour does not appear in the printed description on our demo receipt. That is why three identical \"VÅGSJÖN washclth 30x\" lines are in fact a white washcloth, a light beige one and a dark grey one — a distinction ReFound surfaces when the information is available.",
        },
        {
          q: "Does ReFound work with IKEA receipts?",
          a: "Yes. An IKEA receipt can be scanned and stored like any other, and items are matched to a named product when the information is available, as in the examples on this page.",
        },
        {
          q: "Is ReFound affiliated with IKEA?",
          a: "No. ReFound is an independent app, not affiliated with, endorsed by or sponsored by IKEA. This page only describes how ReFound handles a receipt from that merchant.",
        },
      ],
      ctaTitle: "Know what your IKEA receipt actually says",
      ctaBody:
        "Scan the receipt, keep a clear copy, and find each item — colour included, when the information is available.",
      relatedTitle: "Read next",
      related: [
        { label: "Canadian Tire receipts", href: EN.canadianTire, desc: "Decoding the product codes" },
        { label: "RONA receipts", href: EN.rona, desc: "A thermostat, a warranty and proof of purchase" },
        { label: "Home Depot receipts", href: EN.homeDepot, desc: "Descriptions that are hard to recognize" },
        { label: "Receipt app", href: EN.receipts, desc: "What ReFound does, in short" },
      ],
    },
  },

  /* =========================== 3. HOME DEPOT =========================== */
  {
    key: "home-depot",
    frPath: FR.homeDepot,
    enPath: EN.homeDepot,
    image: "/og-base.png",
    imageAltFr: "Reçu Home Depot numérisé dans ReFound",
    imageAltEn: "Home Depot receipt digitized in ReFound",
    fr: {
      metaTitle: "Reçus Home Depot : décoder les descriptions | ReFound",
      metaDescription:
        "Sur notre reçu Home Depot de démonstration, « XL VT BOT <A> » correspond à de la gomme Wrigley's Excel. Exemples réels de descriptions abrégées identifiées par ReFound.",
      keywords:
        "reçu Home Depot, comprendre un reçu Home Depot, description abrégée reçu, identifier un article sur un reçu, application de reçus",
      breadcrumb: "Reçus Home Depot",
      eyebrow: "Marchands",
      h1: "Vos reçus Home Depot, plus faciles à comprendre",
      intro: [
        "Sur un reçu Home Depot, la description d'un article est réduite à quelques caractères. Certaines lignes restent devinables; d'autres deviennent méconnaissables une fois qu'on a quitté le magasin.",
        "Voici deux exemples tirés d'un reçu Home Depot numérisé dans un compte de démonstration ReFound, avec la ligne exacte imprimée et le produit identifié.",
      ],
      sections: [
        {
          heading: "Que signifie « XL VT BOT <A> » sur notre reçu Home Depot?",
          body: [
            "Sur notre reçu Home Depot de démonstration, la ligne « XL VT BOT <A> » est identifiée par ReFound comme du Chewing-gum sans sucre à la menthe verte Wrigley's Excel Bottle (60 unités).",
          ],
          examples: [
            {
              receiptLine: "XL VT BOT <A>",
              productName: "Chewing-gum sans sucre à la menthe verte Wrigley's Excel Bottle (60 unités)",
              image: {
                src: "/merchants/hd-064900407475.webp",
                alt: "Bouteille de gomme sans sucre Excel menthe verte, 60 unités — photo produit associée par ReFound à la ligne « XL VT BOT <A> »",
              },
              merchantUrl: "https://www.homedepot.ca/produit/1000116866",
              merchantUrlLabel: "Voir la fiche sur homedepot.ca",
            },
          ],
          afterExamples: [
            "C'est l'exemple le plus frappant de ce reçu. Rien dans « XL VT BOT » ne suggère de la gomme à mâcher : « XL » vient d'Excel, « VT » de vert (la saveur menthe verte) et « BOT » de bottle, le format en bouteille. La mention « <A> » apparaît telle quelle dans la description imprimée.",
            "Quelques semaines après l'achat, cette ligne est indéchiffrable. C'est typiquement le genre de montant qu'on n'arrive plus à expliquer en relisant un reçu.",
          ],
        },
        {
          heading: "Que signifie « 1 4\" BLK <A> chithe » sur notre reçu Home Depot?",
          body: [
            "Sur notre reçu Home Depot de démonstration, la ligne « 1 4\" BLK <A> chithe » est identifiée par ReFound comme un 4-inch Black House Number 1 - 1pc, c'est-à-dire un chiffre de maison « 1 » noir de 4 pouces.",
          ],
          examples: [
            {
              receiptLine: "1 4\" BLK <A> chithe",
              productName: "4-inch Black House Number 1 - 1pc",
              details: ["Chiffre de maison « 1 », noir, 4 pouces, vendu à l'unité"],
              merchantUrl:
                "https://www.homedepot.ca/product/hillman-4-inch-black-house-number-1-1pc/1000830552",
              merchantUrlLabel: "Voir la fiche sur homedepot.ca",
            },
          ],
          afterExamples: [
            "Cette ligne mélange le chiffre acheté (1), la dimension (4\"), la couleur abrégée (BLK) et des fragments de mots. Le « 1 » du début n'est pas une quantité : c'est le chiffre lui-même. Sans le produit identifié, la confusion est facile.",
          ],
        },
        {
          heading: "Ce que ReFound ajoute à la simple lecture d'un reçu",
          body: [
            "Un OCR lit « XL VT BOT <A> » et s'arrête là. La chaîne est exacte, mais elle ne dit pas ce qui a été acheté.",
            "ReFound conserve la ligne imprimée telle quelle, puis y associe un produit reconnaissable lorsque l'information est disponible, avec une photo et un lien vers la fiche du marchand quand ils existent — c'est ce qui rend un reçu utile plusieurs semaines après l'achat. Voir [l'application de reçus](/application-recus) et [la précision OCR](/fonctionnalites/precision-ocr).",
          ],
          afterExamples: [
            "L'identification dépend du marchand, du produit et des données disponibles : ReFound n'identifie pas nécessairement tous les articles de tous les reçus.",
          ],
        },
        DISCLAIMER_FR("Home Depot"),
      ],
      faqTitle: "Questions fréquentes sur les reçus Home Depot",
      faq: [
        {
          q: "ReFound fonctionne-t-il avec les reçus Home Depot?",
          a: "Oui. Un reçu Home Depot peut être numérisé et conservé comme n'importe quel autre reçu, et les articles sont associés à un produit nommé lorsque l'information est disponible, comme dans les deux exemples de cette page.",
        },
        {
          q: "Comment savoir à quoi correspond une description abrégée sur un reçu Home Depot?",
          a: "La description imprimée est tronquée et ne reprend pas le nom commercial du produit. Sur notre reçu de démonstration, « XL VT BOT <A> » correspond à du Chewing-gum sans sucre à la menthe verte Wrigley's Excel Bottle (60 unités) : le lien n'est pas devinable à la lecture seule.",
        },
        {
          q: "ReFound est-il affilié à Home Depot?",
          a: "Non. ReFound est une application indépendante, qui n'est ni affiliée, ni approuvée, ni commanditée par Home Depot. Cette page décrit seulement la façon dont ReFound traite un reçu de ce marchand.",
        },
      ],
      ctaTitle: "Gardez vos reçus Home Depot compréhensibles",
      ctaBody:
        "Numérisez le reçu et gardez une trace claire de ce que vous avez acheté, même quand la description imprimée ne veut plus rien dire.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Reçus Canadian Tire", href: FR.canadianTire, desc: "Décoder les codes produits" },
        { label: "Reçus RONA", href: FR.rona, desc: "Un thermostat, une garantie et une preuve d'achat" },
        { label: "Reçus IKEA", href: FR.ikea, desc: "Trois lignes identiques, trois produits différents" },
        { label: "Retours en magasin", href: FR.returns, desc: "Retrouver et présenter le bon reçu" },
      ],
    },
    en: {
      metaTitle: "Home Depot receipts: decoding the descriptions | ReFound",
      metaDescription:
        "On our demo Home Depot receipt, \"XL VT BOT <A>\" turns out to be Wrigley's Excel gum. Real examples of abbreviated descriptions identified by ReFound.",
      keywords:
        "Home Depot receipt, read a Home Depot receipt, receipt abbreviation meaning, identify item on receipt, receipt app",
      breadcrumb: "Home Depot receipts",
      eyebrow: "Merchants",
      h1: "Making sense of your Home Depot receipts",
      intro: [
        "On a Home Depot receipt, an item's description is cut down to a few characters. Some lines are still guessable; others become unrecognizable the moment you leave the store.",
        "Here are two examples from a Home Depot receipt scanned into a ReFound demo account, with the exact printed line and the identified product.",
      ],
      sections: [
        {
          heading: "What does \"XL VT BOT <A>\" mean on our Home Depot receipt?",
          body: [
            "On our demo Home Depot receipt, the line \"XL VT BOT <A>\" is identified by ReFound as Wrigley's Excel Bottle Spearmint Sugar Free Gum 60s.",
          ],
          examples: [
            {
              receiptLine: "XL VT BOT <A>",
              productName: "Wrigley's Excel Bottle Spearmint Sugar Free Gum 60s",
              image: {
                src: "/merchants/hd-064900407475.webp",
                alt: "Excel spearmint sugar-free gum bottle, 60 pieces — product photo ReFound attached to the \"XL VT BOT <A>\" line",
              },
              merchantUrl: "https://www.homedepot.ca/product/1000116866",
              merchantUrlLabel: "View on homedepot.ca",
            },
          ],
          afterExamples: [
            "This is the most striking line on that receipt. Nothing in \"XL VT BOT\" suggests chewing gum: \"XL\" comes from Excel, \"VT\" from vert (French for green, the spearmint flavour) and \"BOT\" from the bottle format. The \"<A>\" marker appears as-is in the printed description.",
            "A few weeks after the purchase, that line is unreadable. It is exactly the kind of amount people can no longer account for when they go back through a receipt.",
          ],
        },
        {
          heading: "What does \"1 4\" BLK <A> chithe\" mean on our Home Depot receipt?",
          body: [
            "On our demo Home Depot receipt, the line \"1 4\" BLK <A> chithe\" is identified by ReFound as a 4-inch Black House Number 1 - 1pc.",
          ],
          examples: [
            {
              receiptLine: "1 4\" BLK <A> chithe",
              productName: "4-inch Black House Number 1 - 1pc",
              details: ["House number \"1\", black, 4 inches, sold individually"],
              merchantUrl:
                "https://www.homedepot.ca/product/hillman-4-inch-black-house-number-1-1pc/1000830552",
              merchantUrlLabel: "View on homedepot.ca",
            },
          ],
          afterExamples: [
            "This line mixes the digit that was bought (1), the size (4\"), an abbreviated colour (BLK) and leftover word fragments. The leading \"1\" is not a quantity — it is the house number itself. Without the identified product, that is easy to misread.",
          ],
        },
        {
          heading: "What ReFound adds on top of plain receipt reading",
          body: [
            "OCR reads \"XL VT BOT <A>\" and stops there. The string is accurate, but it does not say what was bought.",
            "ReFound keeps the printed line exactly as it is, then attaches a recognizable product when the information is available, with a photo and a link to the merchant's product page when they exist — which is what makes a receipt useful weeks after the purchase. See [the receipt app](/en/receipt-app) and [OCR accuracy](/en/features/ocr-accuracy).",
          ],
          afterExamples: [
            "Identification depends on the merchant, the product and the data available: ReFound does not necessarily identify every item on every receipt.",
          ],
        },
        DISCLAIMER_EN("Home Depot"),
      ],
      faqTitle: "Home Depot receipt questions",
      faq: [
        {
          q: "Does ReFound work with Home Depot receipts?",
          a: "Yes. A Home Depot receipt can be scanned and stored like any other, and items are matched to a named product when the information is available, as in the two examples on this page.",
        },
        {
          q: "How do I find out what an abbreviated Home Depot receipt line refers to?",
          a: "The printed description is truncated and does not repeat the product's commercial name. On our demo receipt, \"XL VT BOT <A>\" is Wrigley's Excel Bottle Spearmint Sugar Free Gum 60s — a match you cannot guess from the printed line alone.",
        },
        {
          q: "Is ReFound affiliated with Home Depot?",
          a: "No. ReFound is an independent app, not affiliated with, endorsed by or sponsored by Home Depot. This page only describes how ReFound handles a receipt from that merchant.",
        },
      ],
      ctaTitle: "Keep your Home Depot receipts understandable",
      ctaBody:
        "Scan the receipt and keep a clear record of what you bought, even when the printed description no longer means anything.",
      relatedTitle: "Read next",
      related: [
        { label: "Canadian Tire receipts", href: EN.canadianTire, desc: "Decoding the product codes" },
        { label: "RONA receipts", href: EN.rona, desc: "A thermostat, a warranty and proof of purchase" },
        { label: "IKEA receipts", href: EN.ikea, desc: "Three identical lines, three different products" },
        { label: "In-store returns", href: EN.returns, desc: "Finding and showing the right receipt" },
      ],
    },
  },

  /* ============================== 4. RONA ============================== */
  {
    key: "rona",
    frPath: FR.rona,
    enPath: EN.rona,
    image: "/og-base.png",
    imageAltFr: "Reçu RONA numérisé dans ReFound",
    imageAltEn: "RONA receipt digitized in ReFound",
    fr: {
      metaTitle: "Reçus RONA : identifier un achat et sa garantie | ReFound",
      metaDescription:
        "Sur notre reçu RONA de démonstration, « THERMOSTAT INT/SMRTSENSOR PREM » correspond à un thermostat Ecobee Smart Premium identifié par ReFound, avec son information de garantie.",
      keywords:
        "reçu RONA, comprendre un reçu RONA, preuve d'achat rénovation, garantie thermostat, identifier un article sur un reçu, application de reçus",
      breadcrumb: "Reçus RONA",
      eyebrow: "Marchands",
      h1: "Vos reçus RONA, plus faciles à comprendre",
      intro: [
        "Un reçu de quincaillerie contient souvent quelques articles à faible valeur… et un achat important qu'il faudra retrouver des années plus tard. C'est le cas de notre reçu RONA de démonstration.",
        "Voici la ligne exacte telle qu'imprimée, le produit que ReFound a identifié, et pourquoi cette différence compte au moment d'une garantie.",
      ],
      sections: [
        {
          heading: "Que signifie « THERMOSTAT INT/SMRTSENSOR PREM » sur notre reçu RONA?",
          body: [
            "Sur notre reçu RONA de démonstration, la ligne « THERMOSTAT INT/SMRTSENSOR PREM » est identifiée par ReFound comme un Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor.",
          ],
          examples: [
            {
              receiptLine: "THERMOSTAT INT/SMRTSENSOR PREM",
              productName: "Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor",
              image: {
                src: "/merchants/rona-43125011.jpg",
                alt: "Thermostat intelligent ecobee Smart Premium noir avec capteur de pièce — photo produit associée par ReFound à la ligne « THERMOSTAT INT/SMRTSENSOR PREM »",
              },
              warrantyBadge: "Garantie limitée 3 ans",
              warranty:
                "Dans le compte démo, ReFound rattache à ce produit une garantie du fabricant de 3 ans (garantie limitée), à partir de la date d'achat inscrite sur le reçu.",
              merchantUrl:
                "https://www.rona.ca/en/product/ecobee-smart-premium-black-wi-fi-compatible-thermostat-and-room-sensor-eb-state6c-01-43125011",
              merchantUrlLabel: "Voir la fiche sur rona.ca",
            },
          ],
          afterExamples: [
            "La description imprimée indique bien la catégorie — un thermostat intelligent vendu avec un capteur de pièce — mais ni la marque, ni le modèle, ni la couleur. Or c'est exactement ce qu'on doit fournir pour une réclamation de garantie ou un appel au service technique.",
            "Sur ce reçu de démonstration, la description imprimée est d'ailleurs plus explicite que celles observées sur nos reçus Canadian Tire ou Home Depot : « THERMOSTAT » reste lisible. Cela ne suffit toutefois pas à identifier le produit acheté.",
          ],
        },
        {
          heading: "Pourquoi un reçu RONA conservé devient plus utile avec le temps",
          body: [
            "Un thermostat intelligent est un achat qu'on installe et qu'on oublie. Le problème arrive deux ans plus tard, quand l'appareil cesse de fonctionner : il faut alors retrouver la date d'achat, le montant payé et le modèle exact.",
            "Le reçu papier, lui, s'efface. Le papier thermique pâlit, et la ligne « THERMOSTAT INT/SMRTSENSOR PREM » n'aurait de toute façon pas suffi à identifier le modèle.",
            "Une copie numérique conservée règle la première partie du problème : la preuve d'achat datée. L'identification du produit règle la seconde : savoir de quel appareil il s'agit. Sur cet exemple, ReFound dispose des deux, ainsi que d'une garantie du fabricant de 3 ans rattachée au produit — voir [le suivi des garanties](/application-garanties).",
          ],
          bullets: [
            "La date et le montant restent lisibles même si le papier s'efface",
            "Le produit identifié permet de nommer précisément l'appareil",
            "L'information de garantie est associée quand elle existe pour ce produit",
            "Le reçu reste utilisable comme preuve d'achat pour un [retour en magasin](/application-retours-magasin)",
          ],
        },
        {
          heading: "Ce que ReFound ajoute à la simple lecture d'un reçu",
          body: [
            "Un OCR lit correctement « THERMOSTAT INT/SMRTSENSOR PREM » (voir [la précision OCR](/fonctionnalites/precision-ocr)) et s'arrête là. On sait qu'il s'agit d'un thermostat; on ne sait pas lequel.",
            "ReFound conserve la ligne imprimée telle quelle et y associe, lorsque l'information est disponible, un produit nommé, sa photo, un lien vers sa fiche chez le marchand et l'information de garantie correspondante. C'est ce qui transforme un reçu conservé en preuve d'achat réellement utilisable.",
          ],
          afterExamples: [
            "L'identification et l'information de garantie dépendent du marchand, du produit et des données disponibles : ReFound n'identifie pas nécessairement tous les articles de tous les reçus.",
          ],
        },
        DISCLAIMER_FR("RONA"),
      ],
      faqTitle: "Questions fréquentes sur les reçus RONA",
      faq: [
        {
          q: "ReFound fonctionne-t-il avec les reçus RONA?",
          a: "Oui. Un reçu RONA peut être numérisé et conservé comme n'importe quel autre reçu, et les articles sont associés à un produit nommé lorsque l'information est disponible, comme dans l'exemple de cette page.",
        },
        {
          q: "Comment retrouver la preuve d'achat d'un appareil acheté chez RONA il y a deux ans?",
          a: "Il faut une copie lisible du reçu, avec la date et le montant, et l'identification du produit. Sur notre reçu RONA de démonstration, la ligne imprimée « THERMOSTAT INT/SMRTSENSOR PREM » est associée par ReFound à un Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor, ce qui rend l'appareil identifiable des années après l'achat.",
        },
        {
          q: "ReFound est-il affilié à RONA?",
          a: "Non. ReFound est une application indépendante, qui n'est ni affiliée, ni approuvée, ni commanditée par RONA. Cette page décrit seulement la façon dont ReFound traite un reçu de ce marchand.",
        },
      ],
      ctaTitle: "Gardez vos preuves d'achat de rénovation",
      ctaBody:
        "Numérisez le reçu au moment de l'achat, et retrouvez l'appareil, la date et le montant quand la garantie devient utile.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Reçus Canadian Tire", href: FR.canadianTire, desc: "Décoder les codes produits" },
        { label: "Reçus Home Depot", href: FR.homeDepot, desc: "Des descriptions difficiles à reconnaître" },
        { label: "Reçus IKEA", href: FR.ikea, desc: "Trois lignes identiques, trois produits différents" },
        { label: "Garanties et preuves d'achat", href: FR.warranties, desc: "Garder ce qu'il faut, au bon endroit" },
      ],
    },
    en: {
      metaTitle: "RONA receipts: identifying a purchase and its warranty | ReFound",
      metaDescription:
        "On our demo RONA receipt, \"THERMOSTAT INT/SMRTSENSOR PREM\" is an Ecobee Smart Premium thermostat identified by ReFound, together with its warranty information.",
      keywords:
        "RONA receipt, read a RONA receipt, proof of purchase home improvement, thermostat warranty, identify item on receipt, receipt app",
      breadcrumb: "RONA receipts",
      eyebrow: "Merchants",
      h1: "Making sense of your RONA receipts",
      intro: [
        "A hardware store receipt often holds a few low-value items… and one significant purchase you will need to find again years later. That is the case on our demo RONA receipt.",
        "Here is the exact printed line, the product ReFound identified, and why that difference matters when a warranty comes into play.",
      ],
      sections: [
        {
          heading: "What does \"THERMOSTAT INT/SMRTSENSOR PREM\" mean on our RONA receipt?",
          body: [
            "On our demo RONA receipt, the line \"THERMOSTAT INT/SMRTSENSOR PREM\" is identified by ReFound as an Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor.",
          ],
          examples: [
            {
              receiptLine: "THERMOSTAT INT/SMRTSENSOR PREM",
              productName: "Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor",
              image: {
                src: "/merchants/rona-43125011.jpg",
                alt: "ecobee Smart Premium black smart thermostat with room sensor — product photo ReFound attached to the \"THERMOSTAT INT/SMRTSENSOR PREM\" line",
              },
              warrantyBadge: "3-year limited warranty",
              warranty:
                "In the demo account ReFound links a 3-year limited manufacturer warranty to this product, starting from the purchase date printed on the receipt.",
              merchantUrl:
                "https://www.rona.ca/en/product/ecobee-smart-premium-black-wi-fi-compatible-thermostat-and-room-sensor-eb-state6c-01-43125011",
              merchantUrlLabel: "View on rona.ca",
            },
          ],
          afterExamples: [
            "The printed description does give away the category — a smart thermostat sold with a room sensor — but not the brand, the model or the colour. Those are precisely what a warranty claim or a support call asks for.",
            "On this demo receipt the printed description is actually more explicit than the ones we saw on our Canadian Tire and Home Depot receipts: \"THERMOSTAT\" survives intact. It still is not enough to identify the product that was bought.",
          ],
        },
        {
          heading: "Why a stored RONA receipt gets more useful over time",
          body: [
            "A smart thermostat is the kind of purchase you install and forget. The problem shows up two years later, when the device stops working and you need the purchase date, the amount paid and the exact model.",
            "Paper does not help: thermal receipts fade, and \"THERMOSTAT INT/SMRTSENSOR PREM\" would not have identified the model anyway.",
            "A stored digital copy solves the first half — dated proof of purchase. Product identification solves the second — knowing which device it is. In this example ReFound has both, plus a 3-year manufacturer warranty attached to the product; see [warranty tracking](/en/warranty-tracker-app).",
          ],
          bullets: [
            "The date and amount stay readable even as the paper fades",
            "The identified product lets you name the device precisely",
            "Warranty information is attached when it exists for that product",
            "The receipt stays usable as proof of purchase for an [in-store return](/en/returns-app)",
          ],
        },
        {
          heading: "What ReFound adds on top of plain receipt reading",
          body: [
            "OCR reads \"THERMOSTAT INT/SMRTSENSOR PREM\" correctly (see [OCR accuracy](/en/features/ocr-accuracy)) and stops there. You know it was a thermostat; you do not know which one.",
            "ReFound keeps the printed line as it is and attaches, when the information is available, a named product, its photo, a link to the merchant's product page and the matching warranty information. That is what turns a stored receipt into proof of purchase you can actually use.",
          ],
          afterExamples: [
            "Identification and warranty information depend on the merchant, the product and the data available: ReFound does not necessarily identify every item on every receipt.",
          ],
        },
        DISCLAIMER_EN("RONA"),
      ],
      faqTitle: "RONA receipt questions",
      faq: [
        {
          q: "Does ReFound work with RONA receipts?",
          a: "Yes. A RONA receipt can be scanned and stored like any other, and items are matched to a named product when the information is available, as in the example on this page.",
        },
        {
          q: "How do I find proof of purchase for an appliance bought at RONA two years ago?",
          a: "You need a readable copy of the receipt, with the date and amount, plus the product identification. On our demo RONA receipt, the printed line \"THERMOSTAT INT/SMRTSENSOR PREM\" is matched by ReFound to an Ecobee Smart Premium Black Wi-Fi Compatible Thermostat and Room Sensor, which keeps the device identifiable years after the purchase.",
        },
        {
          q: "Is ReFound affiliated with RONA?",
          a: "No. ReFound is an independent app, not affiliated with, endorsed by or sponsored by RONA. This page only describes how ReFound handles a receipt from that merchant.",
        },
      ],
      ctaTitle: "Keep the proof of purchase for your home projects",
      ctaBody:
        "Scan the receipt when you buy, and find the device, the date and the amount when the warranty finally matters.",
      relatedTitle: "Read next",
      related: [
        { label: "Canadian Tire receipts", href: EN.canadianTire, desc: "Decoding the product codes" },
        { label: "Home Depot receipts", href: EN.homeDepot, desc: "Descriptions that are hard to recognize" },
        { label: "IKEA receipts", href: EN.ikea, desc: "Three identical lines, three different products" },
        { label: "Warranties & proof of purchase", href: EN.warranties, desc: "Keep what matters, in one place" },
      ],
    },
  },
];

export function getMerchantPage(key: string): PillarPage | undefined {
  return merchantPages.find((m) => m.key === key);
}

/** Links used to weave the merchant pages into existing merchant mentions. */
export const MERCHANT_LINKS = { fr: FR, en: EN };

/** Display name per page key, used in metadata and structured data. */
export const MERCHANT_NAMES: Record<string, string> = {
  "canadian-tire": "Canadian Tire",
  ikea: "IKEA",
  "home-depot": "Home Depot",
  rona: "RONA",
};
